import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { eur } from "./shopFloorFormat";
import { useT } from "../../i18n/LanguageProvider";

const SALE_CONFIRMATION_SOUND = "/sounds/sale-confirmation.mp3";
/** OD-18 is binding: 2.5 s auto-dismiss — never lengthened, never tap-to-dismiss. */
const DISMISS_MS = 2500;

/**
 * THE "Sale complete" moment — one implementation for every surface that logs
 * a floor sale (the till and the bouquet checkout). Owns the portal, the
 * OD-18 timer and the confirmation sound, so the cue a florist learns is the
 * same cue everywhere (code-review 2026-08-09: the copied inline version had
 * shipped silent while the till chimed).
 */
export function SaleConfirmationOverlay({
  commissionPercent,
  commissionEarnedEur,
  onDismiss,
}: {
  commissionPercent: string | null;
  commissionEarnedEur: string;
  onDismiss: () => void;
}) {
  const { t } = useT();
  // The dismiss callback must not restart the timer if the host re-renders.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    const audio = new Audio(SALE_CONFIRMATION_SOUND);
    void audio.play().catch(() => undefined);
    const timer = window.setTimeout(() => dismissRef.current(), DISMISS_MS);
    return () => {
      window.clearTimeout(timer);
      audio.pause();
    };
  }, []);

  return createPortal(
    <div
      className="shop-floor-sale-confirmation-layer"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="shop-floor-sale-confirmation-card">
        <span className="shop-floor-sale-confirmation-icon" aria-hidden="true">
          <ShopFloorIcon name="check" size={28} />
        </span>
        <p className="shop-floor-sale-confirmation-title">{t("saleDone.title")}</p>
        <strong className="shop-floor-sale-confirmation-amount">
          {eur(Number(commissionEarnedEur))}
        </strong>
        <p className="shop-floor-sale-confirmation-caption">
          {commissionPercent == null || Number(commissionPercent) === 0
            ? t("saleDone.noCommission")
            : t("saleDone.addedToCommissions")}
        </p>
      </div>
    </div>,
    document.body,
  );
}
